import {
  IsOptional,
  IsString,
  IsBoolean,
  IsArray,
  IsNumber,
  IsNotEmpty,
  ValidateIf,
  IsEmail,
} from 'class-validator';
import { Asset } from './asset-types';

// Banner crop data interface for storing crop coordinates
export interface CropData {
  x1: number; // Left edge of crop area
  y1: number; // Top edge of crop area
  x2: number; // Right edge of crop area
  y2: number; // Bottom edge of crop area
}

// Consolidated SiteSettings interface (matches Post architecture pattern)
export interface SiteSettings {
  siteId: string;
  siteName?: string;
  slug?: string;
  arConnectSlug?: string | null;
  primaryColor?: string;
  fontFamily?: string; // Site font family
  logo?: Asset; // Full Asset object after hydration (like Post.title.backgroundImage)
  banner?: Asset; // Site banner image
  bannerCrop?: CropData;
  hideNameInHeader?: boolean;
  allowedAnalystGroupIds?: number[];
  availablePostTypes: string[]; // List of available post type tags for this site
  availableTopics: string[]; // List of available topic tags for this site
  featuredTopics: string[]; // Ordered list of featured topics to display on portal home page
  generalInfo?: string; // General information rich text content
  // General tab settings
  portalDiscoverable?: boolean; // Make portal discoverable to analysts
  contentAlertsEnabled?: boolean; // Enable content alerts
  contactFormRecipients?: string[]; // Email addresses that receive contact form submissions
  // Content tab settings
  featuredContentCategory?: string; // Featured content category selection
  displayFeaturedThumbnail?: boolean; // Display featured content image as thumbnail
  displayFeaturedFullSize?: boolean; // Display featured content image full-size
  displayFeaturedDate?: boolean; // Display date on featured content items
  displayFeaturedType?: boolean; // Display type on featured content items
  showContentImages?: boolean; // Show images on content items
  displayShareOptions?: boolean; // Display share options
  // Vendor portal fields
  vendorName?: string; // Display name for the vendor
  updatedAt: string;
  createdAt: string;
}

// Logo upload DTOs
export interface LogoUploadRequestDTO {
  fileExtension: string;
  contentType: string;
}

export interface LogoUploadResponseDTO {
  uploadUrl: string;
  fileUrl: string;
  key: string;
  prefixedKey: string;
}

// Site Settings DTOs for API operations
export class CreateSiteSettingsDto {
  @IsString()
  @IsNotEmpty()
  siteId: string;

  @IsOptional()
  @IsString()
  primaryColor?: string;

  @IsOptional()
  @IsString()
  fontFamily?: string;

  @IsOptional()
  @ValidateIf((o) => o.logoAssetId !== null)
  @IsString()
  logoAssetId?: string | null; // Asset ID for logo (null = remove, undefined = no change, string = set)

  @IsOptional()
  @ValidateIf((o) => o.bannerAssetId !== null)
  @IsString()
  bannerAssetId?: string | null; // Asset ID for site banner (null = remove, undefined = no change, string = set)

  @IsOptional()
  bannerCrop?: CropData | null; // null = clear crop

  @IsOptional()
  @IsBoolean()
  hideNameInHeader?: boolean;

  @IsOptional()
  @IsArray()
  @IsNumber({}, { each: true })
  allowedAnalystGroupIds?: number[];

  @IsArray()
  @IsString({ each: true })
  availablePostTypes: string[];

  @IsArray()
  @IsString({ each: true })
  availableTopics: string[];

  @IsArray()
  @IsString({ each: true })
  featuredTopics: string[];

  @IsOptional()
  @IsString()
  generalInfo?: string;

  @IsOptional()
  @IsBoolean()
  portalDiscoverable?: boolean;

  @IsOptional()
  @IsBoolean()
  contentAlertsEnabled?: boolean;

  @IsOptional()
  @IsArray()
  @IsEmail({}, { each: true })
  contactFormRecipients?: string[];

  @IsOptional()
  @IsString()
  featuredContentCategory?: string;

  @IsOptional()
  @IsBoolean()
  displayFeaturedThumbnail?: boolean;

  @IsOptional()
  @IsBoolean()
  displayFeaturedFullSize?: boolean;

  @IsOptional()
  @IsBoolean()
  displayFeaturedDate?: boolean;

  @IsOptional()
  @IsBoolean()
  displayFeaturedType?: boolean;

  @IsOptional()
  @IsBoolean()
  showContentImages?: boolean;

  @IsOptional()
  @IsBoolean()
  displayShareOptions?: boolean;

  @IsOptional()
  @IsString()
  vendorName?: string;
}

export class UpdateSiteSettingsDto {
  @IsOptional()
  @IsString()
  primaryColor?: string;

  @IsOptional()
  @IsString()
  fontFamily?: string;

  @IsOptional()
  @IsString()
  logoAssetId?: string;

  @IsOptional()
  @IsString()
  bannerAssetId?: string;

  @IsOptional()
  bannerCrop?: CropData | null; // null = clear crop

  @IsOptional()
  @IsBoolean()
  hideNameInHeader?: boolean;

  @IsOptional()
  @IsArray()
  @IsNumber({}, { each: true })
  allowedAnalystGroupIds?: number[];

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  availablePostTypes?: string[];

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  availableTopics?: string[];

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  featuredTopics?: string[];

  @IsOptional()
  @IsString()
  generalInfo?: string;

  @IsOptional()
  @IsBoolean()
  portalDiscoverable?: boolean;

  @IsOptional()
  @IsBoolean()
  contentAlertsEnabled?: boolean;

  @IsOptional()
  @IsArray()
  @IsEmail({}, { each: true })
  contactFormRecipients?: string[];

  @IsOptional()
  @IsString()
  featuredContentCategory?: string;

  @IsOptional()
  @IsBoolean()
  displayFeaturedThumbnail?: boolean;

  @IsOptional()
  @IsBoolean()
  displayFeaturedFullSize?: boolean;

  @IsOptional()
  @IsBoolean()
  displayFeaturedDate?: boolean;

  @IsOptional()
  @IsBoolean()
  displayFeaturedType?: boolean;

  @IsOptional()
  @IsBoolean()
  showContentImages?: boolean;

  @IsOptional()
  @IsBoolean()
  displayShareOptions?: boolean;

  @IsOptional()
  @IsString()
  vendorName?: string;
}

// Form value interfaces for frontend state management
export interface GeneralFormValue {
  generalInfo?: string;
  portalDiscoverable?: boolean;
  contentAlertsEnabled?: boolean;
  contactFormRecipients?: string[];
}

export interface BrandFormValue {
  primaryColor?: string;
  fontFamily?: string;
}

export interface ContentFormValue {
  featuredContentCategory?: string;
  displayFeaturedThumbnail?: boolean;
  displayFeaturedFullSize?: boolean;
  displayFeaturedDate?: boolean;
  displayFeaturedType?: boolean;
  showContentImages?: boolean;
  displayShareOptions?: boolean;
}

// Type aliases for frontend compatibility
export type SiteSettingsDTO = SiteSettings;
export type UpdateSiteSettingsDTO = UpdateSiteSettingsDto;
